import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, TextField,
  InputAdornment, CircularProgress, Alert, Select, FormControl, InputLabel,
  Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  MenuItem, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import iwalkcvService, { IWReserva } from '../../services/iwalkcv.service';

const PAG_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  PAID: 'success',
  PENDING: 'warning',
  FAILED: 'error',
  CANCELLED: 'error',
  REFUNDED: 'default',
};

const PAG_LABEL: Record<string, string> = {
  PAID: 'Pago',
  PENDING: 'Pendente',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Reembolsado',
};

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  CONFIRMED: 'success',
  PENDING: 'warning',
  CANCELLED: 'error',
  NO_SHOW: 'error',
  COMPLETED: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: 'Confirmada',
  PENDING: 'Pendente',
  CANCELLED: 'Cancelada',
  NO_SHOW: 'Não compareceu',
  COMPLETED: 'Concluída',
};

type ActionType = 'cancel' | 'complete' | 'noshow';

interface ConfirmDialog {
  type: ActionType;
  reserva: IWReserva;
}

export default function ReservasPage() {
  const [reservas, setReservas] = useState<IWReserva[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPagamento, setFilterPagamento] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Dialog de confirmação de acção
  const [confirm, setConfirm] = useState<ConfirmDialog | null>(null);
  const [acting, setActing] = useState(false);
  const [actError, setActError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await iwalkcvService.getReservas({
        page: page + 1,
        limit: rowsPerPage,
        status: filterStatus || undefined,
        pagamento_status: filterPagamento || undefined,
        search: search || undefined,
      });
      setReservas(res.data || []);
      setTotal(res.total ?? res.data?.length ?? 0);
    } catch {
      setError('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, filterPagamento, search]);

  useEffect(() => { load(); }, [load]);

  function openConfirm(type: ActionType, reserva: IWReserva) {
    setConfirm({ type, reserva });
    setActError(null);
  }

  async function handleAction() {
    if (!confirm) return;
    setActing(true);
    setActError(null);

    const { type, reserva } = confirm;
    const payload: { status?: string; pagamento_status?: string } =
      type === 'cancel'
        ? { status: 'CANCELLED', pagamento_status: 'CANCELLED' }
        : type === 'complete'
        ? { status: 'COMPLETED' }
        : { status: 'NO_SHOW' };

    try {
      await iwalkcvService.updateReservaStatus(reserva.id, payload);
      setConfirm(null);
      load();
    } catch (e: any) {
      setActError(e?.response?.data?.error || 'Erro ao actualizar reserva');
    } finally {
      setActing(false);
    }
  }

  const ACTION_LABELS: Record<ActionType, { title: string; button: string; color: 'error' | 'success' | 'warning' }> = {
    cancel:   { title: 'Cancelar reserva?',          button: 'Cancelar reserva', color: 'error' },
    complete: { title: 'Marcar como concluída?',     button: 'Concluir',         color: 'success' },
    noshow:   { title: 'Marcar como não compareceu?', button: 'Confirmar',       color: 'warning' },
  };

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={700} mb={3}>Reservas</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filtros */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Pesquisar referência, experiência..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={filterStatus}
            label="Estado"
            onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="CONFIRMED">Confirmada</MenuItem>
            <MenuItem value="PENDING">Pendente</MenuItem>
            <MenuItem value="CANCELLED">Cancelada</MenuItem>
            <MenuItem value="COMPLETED">Concluída</MenuItem>
            <MenuItem value="NO_SHOW">Não compareceu</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Pagamento</InputLabel>
          <Select
            value={filterPagamento}
            label="Pagamento"
            onChange={(e) => { setFilterPagamento(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="PAID">Pago</MenuItem>
            <MenuItem value="PENDING">Pendente</MenuItem>
            <MenuItem value="FAILED">Falhou</MenuItem>
            <MenuItem value="CANCELLED">Cancelado</MenuItem>
            <MenuItem value="REFUNDED">Reembolsado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {['Referência', 'Experiência', 'Operador', 'Data/Hora', 'Participantes', 'Total CVE', 'Pagamento', 'Estado', 'Ações'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reservas.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                        fontSize="0.8rem"
                      >
                        {r.referencia}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {r.exp_titulo || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.nome_empresa || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.data_exp
                        ? `${r.data_exp}${r.hora_exp ? ` ${r.hora_exp}` : ''}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.num_adultos}A{r.num_criancas > 0 ? ` + ${r.num_criancas}C` : ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {Number(r.total_cvescudos).toLocaleString('pt-CV')} CVE
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PAG_LABEL[r.pagamento_status] ?? r.pagamento_status}
                        color={PAG_COLOR[r.pagamento_status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[r.status] ?? r.status}
                        color={STATUS_COLOR[r.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        {/* Marcar concluída — só se CONFIRMED */}
                        {r.status === 'CONFIRMED' && (
                          <Tooltip title="Marcar concluída">
                            <IconButton size="small" color="success" onClick={() => openConfirm('complete', r)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Marcar não compareceu — só se CONFIRMED */}
                        {r.status === 'CONFIRMED' && (
                          <Tooltip title="Não compareceu">
                            <IconButton size="small" color="warning" onClick={() => openConfirm('noshow', r)}>
                              <PersonOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Cancelar — se não estiver já CANCELLED ou COMPLETED */}
                        {r.status !== 'CANCELLED' && r.status !== 'COMPLETED' && (
                          <Tooltip title="Cancelar">
                            <IconButton size="small" color="error" onClick={() => openConfirm('cancel', r)}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {reservas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma reserva encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
        </Paper>
      )}

      {/* ── Dialog de confirmação ── */}
      {confirm && (
        <Dialog open onClose={() => !acting && setConfirm(null)} maxWidth="xs">
          <DialogTitle>{ACTION_LABELS[confirm.type].title}</DialogTitle>
          <DialogContent>
            {actError ? (
              <Alert severity="error">{actError}</Alert>
            ) : (
              <Stack spacing={1.5}>
                <Typography>
                  Reserva <strong style={{ fontFamily: 'monospace' }}>{confirm.reserva.referencia}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confirm.reserva.exp_titulo}
                </Typography>
                {confirm.type === 'cancel' && (
                  <Alert severity="warning">
                    O pagamento também será marcado como cancelado.
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)} disabled={acting}>Fechar</Button>
            {!actError && (
              <Button
                variant="contained"
                color={ACTION_LABELS[confirm.type].color}
                onClick={handleAction}
                disabled={acting}
              >
                {acting ? 'A processar...' : ACTION_LABELS[confirm.type].button}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
